import { fromJS, Map } from "immutable";
import { Action, AnyAction } from "redux";
import { combineReducers } from "redux-immutable";

const ShopwerkActionType = {
  SETUP: "integrations/shopwerk/setup",
  DELETE: "integrations/shopwerk/delete",
  GET: "integrations/shopwerk/get",
  RESET_REQUESTS: "integrations/shopwerk/resetRequests"
} as const;

type ShopwerkRequestAction = Action<
  (typeof ShopwerkActionType)[keyof typeof ShopwerkActionType]
> & {
  shopId: number;
  meta: unknown;
} & Record<string, unknown>;

export type ShopwerkDataReduxState = {
  settings?: {
    apiKey: string;
    endpoint: string;
  };
};

type ShopwerkDataReduxStateImmutableState = Map<unknown, unknown>;
const initialImmutableDataState = fromJS({});

type RequestErrorState = "none" | "invalid-key" | "unknown-error";

export type ShopwerkRequestReduxState = {
  loading: boolean;
  success: boolean;
  error: RequestErrorState;
};
type ShopwerkRequestReduxImmutableState = Map<keyof ShopwerkRequestReduxState, unknown>;

const initialRequestState: ShopwerkRequestReduxState = {
  loading: false,
  success: false,
  error: "none"
};
const initialImmutableRequestState = fromJS(initialRequestState);

const dataReducer = (
  state: ShopwerkDataReduxStateImmutableState = initialImmutableDataState,
  action: AnyAction
): ShopwerkDataReduxStateImmutableState => {
  switch (action.type) {
    case ShopwerkActionType.GET:
      return state.merge(fromJS({ settings: action.data.settings }));
    case ShopwerkActionType.DELETE:
      return state.merge(fromJS({ settings: undefined }));
    default:
      return state;
  }
};

export const resetShopwerkIntegrationsRequestsAction = (): AnyAction => {
  return {
    type: ShopwerkActionType.RESET_REQUESTS
  };
};

export const getShopwerkIntegrationSettingsAction = (shopId: number): ShopwerkRequestAction => {
  return {
    type: ShopwerkActionType.GET,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/shopwerk`,
      method: "get"
    }
  };
};
const getReducer = (
  state = initialImmutableRequestState,
  action: AnyAction
): ShopwerkRequestReduxImmutableState => {
  switch (action.type) {
    case ShopwerkActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case ShopwerkActionType.GET:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${ShopwerkActionType.GET}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${ShopwerkActionType.GET}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const deleteShopwerkIntegrationAction = (shopId: number): ShopwerkRequestAction => {
  return {
    type: ShopwerkActionType.DELETE,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/shopwerk`,
      method: "delete",
      error: {
        400: "invalid-key"
      }
    }
  };
};
const deleteReducer = (
  state: ShopwerkRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): ShopwerkRequestReduxImmutableState => {
  switch (action.type) {
    case ShopwerkActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case ShopwerkActionType.DELETE:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${ShopwerkActionType.DELETE}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${ShopwerkActionType.DELETE}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: "unknown-error"
        })
      );
    default:
      return state;
  }
};

export const setupShopwerkIntegrationAction = (
  shopId: number,
  apiKey: string,
  endpoint: string
): ShopwerkRequestAction => {
  return {
    type: ShopwerkActionType.SETUP,
    shopId,
    meta: {
      type: "api",
      url: `${shopId}/integrations/shopwerk`,
      method: "post",
      data: {
        apiKey,
        endpoint
      },
      error: {
        400: "invalid-key"
      }
    }
  };
};
const setupReducer = (
  state: ShopwerkRequestReduxImmutableState = initialImmutableRequestState,
  action: AnyAction
): ShopwerkRequestReduxImmutableState => {
  switch (action.type) {
    case ShopwerkActionType.RESET_REQUESTS:
      return initialImmutableRequestState;
    case ShopwerkActionType.SETUP:
      return state.merge(
        fromJS({
          loading: false,
          success: true,
          error: "none"
        })
      );
    case `${ShopwerkActionType.SETUP}_LOADING`:
      return state.merge(
        fromJS({
          loading: true,
          success: false,
          error: "none"
        })
      );
    case `${ShopwerkActionType.SETUP}_ERROR`:
      return state.merge(
        fromJS({
          loading: false,
          success: false,
          error: action.errorMessage === "invalid-key" ? "invalid-key" : "unknown-error"
        })
      );
    default:
      return state;
  }
};

const requestsReducer = combineReducers({
  get: getReducer,
  setup: setupReducer,
  delete: deleteReducer
});

export const shopwerkIntegrationReducer = combineReducers({
  data: dataReducer,
  requests: requestsReducer
});
